/*
 * Copyright (c) 2021 biocrates life sciences ag, All rights reserved.
 */

import React from "react"

import {Intent, Toaster} from "@blueprintjs/core"
import {IconName} from "@blueprintjs/icons"

import {dismissToastAction, showErrorReport} from "../../actions/core.actions"
import env from "../../context/Env"
import {getManualUrlIfAvailable} from "../../utils/urlUtils"

export const AppToaster = Toaster.create()

export const knownFrontendErrors = {
    "Network Error": `The biocrates ${env(
        "APPLICATION_NAME"
    )} server cannot be reached. Please make sure that your internet connection is working or try again later.`,
}

export type ToastType = "error" | "warning" | "info" | undefined

export interface ToastProps {
    type?: ToastType
    message: string
    details?: string[]
    stacktrace?: string
    timeout?: number
    icon?: IconName
    intent?: Intent
    manualChapterAndId?: {first: string; second: string}
    dispatch: Function
}

const getIcon = (type: ToastType) => {
    switch (type) {
        case "error":
            return "error"
        case "warning":
            return "warning-sign"
        case "info":
            return "info-sign"
        default:
            return null
    }
}

const getIntent = (type: ToastType) => {
    switch (type) {
        case "error":
            return Intent.DANGER
        case "warning":
            return Intent.WARNING
        case "info":
            return Intent.PRIMARY
        default:
            return Intent.NONE
    }
}

export const showToast = (
    {
        type = undefined,
        message,
        details = undefined,
        stacktrace = undefined,
        timeout = undefined,
        icon = undefined,
        intent = undefined,
        manualChapterAndId = undefined,
        dispatch = () => {},
    }: ToastProps,
    key = ""
): string => {
    const getAction = (): any => {
        if (type) {
            switch (type) {
                case "error":
                    return {
                        onClick: () => {
                            dispatch(showErrorReport({message, stacktrace}))
                        },

                        text: "Report",
                    }
                case "warning":
                    if (manualChapterAndId) {
                        const url = getManualUrlIfAvailable(
                            manualChapterAndId.first,
                            manualChapterAndId.second
                        )
                        if (url) {
                            return {
                                onClick: () => window.open(url),
                                icon: "help",
                            }
                        }
                    }
                    return null
                default:
                    return null
            }
        }
        return null
    }

    return AppToaster.show(
        {
            action: getAction(),
            message: (
                <div key={message}>
                    <div style={{marginBottom: "0.5em"}}>{message}</div>
                    {details && (
                        <div style={{fontWeight: 300, listStyleType: "none"}}>
                            <ul>
                                {details.map((value, index) => {
                                    const keyList = index
                                    return <li key={keyList}>{value}</li>
                                })}
                            </ul>
                        </div>
                    )}
                </div>
            ),
            timeout: timeout || 0,
            icon: icon || getIcon(type),
            intent: intent || getIntent(type),
            onDismiss: () => dispatch(dismissToastAction(message)),
        },
        key
    )
}

export default AppToaster
